import {validatePaperExport} from './paperEdit.js';
import {sequenceClips} from './sequence.js';
import {assemblySectionMarkers} from './sectionMarkers.js';

// FCPXML 1.10, NOT the legacy xmeml used by Premiere and Resolve.
// Apple: https://developer.apple.com/documentation/professional-video-applications/creating-fcpxml-documents
// This exports an offline media reference to the ORIGINAL filename; Final Cut
// will need the user's camera originals relinked after import.
const escapeXml=value=>String(value??'').replace(/[&<>"']/g,ch=>({
 '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&apos;'
})[ch]);

const gcd=(a,b)=>{while(b){[a,b]=[b,a%b];}return a||1;};
const rationalSeconds=(numerator,denominator=1)=>{
 if(!Number.isSafeInteger(numerator)||!Number.isSafeInteger(denominator)||denominator<=0||numerator<0)
  throw new Error('Cannot export Final Cut Pro XML: video timing is invalid.');
 if(numerator===0)return '0s';
 const factor=gcd(numerator,denominator);
 const n=numerator/factor,d=denominator/factor;
 return d===1?`${n}s`:`${n}/${d}s`;
};
const rateOk=s=>Number.isSafeInteger(s?.fpsNum)&&Number.isSafeInteger(s?.fpsDen)
 && s.fpsNum>0&&s.fpsDen>0;
const frames=(time,source)=>Math.round(time*source.fpsNum/source.fpsDen);
const framesTime=(count,source)=>rationalSeconds(count*source.fpsDen,source.fpsNum);
const formatKey=s=>[s.width,s.height,s.fpsNum,s.fpsDen].join('/');

const sourceOriginFrames=source=>{
 const label=String(source.timecode||'00:00:00:00');
 const match=/^(\d{2}):([0-5]\d):([0-5]\d)([:;])(\d{2})$/.exec(label);
 if(!match)throw new Error(`Check source timecode for ${source.filename}. Use HH:MM:SS:FF or HH:MM:SS;FF.`);
 const [h,m,s,separator,f]=[Number(match[1]),Number(match[2]),Number(match[3]),match[4],Number(match[5])];
 const nominal=Math.round(source.fpsNum/source.fpsDen);
 if(f>=nominal||h>23)throw new Error(`Invalid original timecode for ${source.filename}.`);
 let count=((h*3600+m*60+s)*nominal)+f;
 if(separator===';'){
  const rate=source.fpsNum+'/'+source.fpsDen;
  if(!['30000/1001','60000/1001'].includes(rate))
   throw new Error(`Drop-frame timecode on ${source.filename} requires 29.97 or 59.94 fps.`);
  const drop=nominal===30?2:4;
  if(s===0&&m%10!==0&&f<drop)
   throw new Error(`Invalid skipped drop-frame timecode on ${source.filename}.`);
  const minutes=h*60+m;
  count-=drop*(minutes-Math.floor(minutes/10));
 }
 return count;
};

export function buildFcpXml(project){
 const paper=project.paperExport;
 if(paper)validatePaperExport(paper);
 const clips=paper?paper.blocks:sequenceClips(project);
 if(!clips?.length)throw new Error('Nothing to export to Final Cut Pro. Add an Assembly passage first.');
 const entries=clips.map((clip,index)=>{
  const source=paper?paper.sources?.[clip.sourceId]:project;
  if(!source||!source.filename||!rateOk(source)
    ||!Number.isSafeInteger(source.width)||source.width<16
    ||!Number.isSafeInteger(source.height)||source.height<16
    ||!Number.isFinite(source.duration)||source.duration<=0
    ||!Number.isSafeInteger(source.audioChannels)||source.audioChannels<0||source.audioChannels>16
    ||!Number.isSafeInteger(source.audioSampleRate)||source.audioSampleRate<=0){
   throw new Error('Cannot export to Final Cut Pro: verify original video filename, frame rate, resolution and audio metadata.');
  }
  if(!['confirmed-cfr','detected-cfr','demo-cfr'].includes(source.frameStatus)){
   throw new Error(`Verify constant frame rate for ${source.filename} before exporting to Final Cut Pro.`);
  }
  if(source.filename.includes('/')||source.filename.includes(String.fromCharCode(92))||
    [...source.filename].some(character=>character.charCodeAt(0)<32))
   throw new Error('Use the original video filename without slashes or control characters in Source settings.');
  const first=frames(clip.start,source),last=frames(clip.end,source);
  if(!Number.isSafeInteger(first)||!Number.isSafeInteger(last)
   ||first<0||last<=first||last>frames(source.duration,source))
   throw new Error(`Cannot export ${source.filename}: a passage is outside its source or shorter than one frame.`);
  return {index,source,sourceId:paper?clip.sourceId:'single',first,last};
 });
 const sequenceSource=entries[0].source;
 const sequenceRate=paper?.sequenceRate || {fpsNum:sequenceSource.fpsNum,fpsDen:sequenceSource.fpsDen};
 const sequence={...sequenceSource,...sequenceRate};
 if(!rateOk(sequence))throw new Error('Confirm the Assembly sequence frame rate before exporting to Final Cut Pro.');

 const formats=new Map();
 const getFormat=source=>{
  const key=formatKey(source);
  if(!formats.has(key))formats.set(key,{id:`r${formats.size+1}`,...source});
  return formats.get(key).id;
 };
 const timelineFormat=getFormat(sequence);
 const assets=new Map();
 for(const entry of entries){
  getFormat(entry.source);
  // Distinct source IDs remain distinct even when camera filenames match.
  if(!assets.has(entry.sourceId))
   assets.set(entry.sourceId,{
    id:`r${1+formats.size+assets.size}`,source:entry.source,
    ordinal:assets.size+1
   });
 }
 // Formats must precede assets. Assign distinct resource IDs after the
 // format set is complete (a later new source format must not collide).
 let cursor=0,elapsed=0;
 const segments=entries.map(entry=>{
  elapsed+=(entry.last-entry.first)*entry.source.fpsDen/entry.source.fpsNum;
  const end=Math.round(elapsed*sequence.fpsNum/sequence.fpsDen);
  if(end<=cursor)throw new Error('A passage is shorter than one Final Cut Pro sequence frame.');
  const segment={...entry,offsetFrame:cursor,durationFrame:end-cursor};
  cursor=end;
  return segment;
 });
 const resources=[...formats.values()].map(format=>
  `    <format id="${format.id}" frameDuration="${rationalSeconds(format.fpsDen,format.fpsNum)}" width="${format.width}" height="${format.height}" fieldOrder="progressive"/>`
 );
 // Asset ids after all formats are assigned, regardless of how many formats.
 const assetRows=[...assets.values()];
 for(let i=0;i<assetRows.length;i++)assetRows[i].id=`r${formats.size+i+1}`;
 for(const entry of assetRows){
  const {id,source,ordinal}=entry;
  const origin=sourceOriginFrames(source);
  // Deliberately unresolvable until the editor chooses Relink Files in FCP.
  // Never put a time-limited Takefold proxy URL here: edits target originals.
  const url=`file:///Takefold-Relink/${ordinal}/${encodeURIComponent(source.filename)}`;
  const hasAudio=source.audioChannels>0;
  resources.push(`    <asset id="${id}" name="${escapeXml(source.filename)}" start="${framesTime(origin,source)}" duration="${framesTime(frames(source.duration,source),source)}" hasVideo="1" format="${getFormat(source)}" hasAudio="${hasAudio?'1':'0'}"${hasAudio?` audioSources="1" audioChannels="${source.audioChannels}" audioRate="${source.audioSampleRate}"`:''}><media-rep kind="original-media" src="${escapeXml(url)}" suggestedFilename="${escapeXml(source.filename)}"/></asset>`);
 }
 // The spine is assembled after resource IDs are settled.
 const chapterMarkers=paper?assemblySectionMarkers(paper,segments.map(segment=>segment.offsetFrame),cursor):[];
 const ordered=segments.map((entry,index)=>{
  const asset=assets.get(entry.sourceId);
  const origin=sourceOriginFrames(entry.source);
  const headings=chapterMarkers.filter(marker=>marker.beforeIndex===index ||
   (index===segments.length-1&&marker.beforeIndex===segments.length));
  const nested=headings.map(marker=>{
   // FCPXML markers are expressed in source time, not edit sequence time.
   // For a final, trailing section, attach to the last visible source frame.
   const last=marker.beforeIndex===segments.length;
   const sourceFrame=origin+(last?entry.last-1:entry.first);
   return `<marker start="${framesTime(sourceFrame,entry.source)}" duration="${framesTime(1,entry.source)}" value="${escapeXml(marker.title)}"/>`;
  }).join('');
  const attrs=`name="${escapeXml(entry.source.filename)}" ref="${asset.id}" offset="${framesTime(entry.offsetFrame,sequence)}" start="${framesTime(origin+entry.first,entry.source)}" duration="${framesTime(entry.durationFrame,sequence)}"${entry.source.audioChannels>0?' audioRole="dialogue"':''}`;
  return nested?`      <asset-clip ${attrs}>${nested}</asset-clip>`:`      <asset-clip ${attrs}/>`;
 }).join('\n');
 return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE fcpxml>
<fcpxml version="1.10">
  <resources>
${resources.join('\n')}
  </resources>
  <event name="Takefold">
    <project name="${escapeXml(project.title||'Takefold Assembly')}">
      <sequence format="${timelineFormat}" duration="${framesTime(cursor,sequence)}" tcStart="0s" tcFormat="NDF" audioLayout="stereo" audioRate="48k">
        <spine>
${ordered}
        </spine>
      </sequence>
    </project>
  </event>
</fcpxml>`;
}
